import {ControlledAutocompleteSelect, LabelRegular} from '@hm/fundamental'
import {useCallback, useMemo, useState} from 'react'

import {MIN_USER_SEARCH_LENGTH, useRiskSurveyTranslation, useUserSuggestions} from '../../hooks'
import type {UserSuggestionDto} from '../../types'

type ResponsiblePersonLookupProps = {
  upmId: string
  value: UserSuggestionDto | null
  onChange: (value: UserSuggestionDto | null) => void
  label: string
  isDisabled?: boolean
}

// Searchable single-user picker limited to users with Risk Survey access to the plant
// (server-side search, same Identity lookup easyPOM uses for its assignee field).
export const ResponsiblePersonLookup = ({
  upmId,
  value,
  onChange,
  label,
  isDisabled = false
}: ResponsiblePersonLookupProps): JSX.Element => {
  const {t} = useRiskSurveyTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const {data: suggestions, isLoading, isError} = useUserSuggestions(upmId, searchTerm)

  // The current assignee is always an option so its name renders before any search.
  const options = useMemo(() => {
    const found = (suggestions ?? []).map((user) => ({value: user.userId, label: user.name}))
    if (value && !found.some((option) => option.value === value.userId)) {
      return [{value: value.userId, label: value.name}, ...found]
    }
    return found
  }, [suggestions, value])

  const handleChange = useCallback(
    (selected: (string | number)[]) => {
      const selectedId = selected[0]
      if (selectedId === undefined) {
        onChange(null)
        return
      }
      const option = options.find((candidate) => candidate.value === selectedId)
      if (option) {
        onChange({userId: String(option.value), name: option.label})
      }
    },
    [options, onChange]
  )

  const statusMessage = (() => {
    if (searchTerm.trim().length < MIN_USER_SEARCH_LENGTH) {
      return t('detail.responsiblePersonMinChars', {count: MIN_USER_SEARCH_LENGTH})
    }
    if (isLoading) return t('detail.searchingUsers')
    if (isError) return t('detail.userSearchFailed')
    if (!suggestions?.length) return t('detail.noUsersFound')
    return undefined
  })()

  return (
    <ControlledAutocompleteSelect
      label={label}
      options={options}
      selectedValues={value ? [value.userId] : []}
      onChange={handleChange}
      onFilterChange={setSearchTerm}
      isDisabled={isDisabled}
      customSelectionComponent={
        statusMessage ? (
          <div className="px-3 py-2" data-cy="responsible-person-lookup-status">
            <LabelRegular className="text-foreground_faded">{statusMessage}</LabelRegular>
          </div>
        ) : undefined
      }
      data-cy="responsible-person-lookup"
    />
  )
}
